/**
 * Impact Effect Manager
 * 
 * Manages arcade-style screen effects for impactful game events.
 * Supports ripple distortion and screen shake with configurable time ramps.
 */

import {
  Scene,
  Camera,
  ArcRotateCamera,
  PostProcess,
  Effect,
  Vector3,
} from '@babylonjs/core';
import { logger } from '../../core/Logger';
import { EventBus } from '../../core/EventBus';
import { resolveTimeRamp, TimeRamps } from './TimeRamps';
import type { 
  RippleConfig, 
  ShakeConfig, 
  ExplosionConfig, 
  ActiveEffect,
  ImpactEffectEvent,
} from './types';
import type { TimeRamp } from './TimeRamps';

// Ripple shader code
const RIPPLE_FRAGMENT_SHADER = `
precision highp float;

varying vec2 vUV;
uniform sampler2D textureSampler;

// Ripple parameters
uniform vec2 uCenter;        // Screen-space center (0-1)
uniform float uTime;         // Normalized time (0-1)
uniform float uAmplitude;    // Maximum distortion
uniform float uFrequency;    // Number of wave rings
uniform float uSpeed;        // Expansion speed
uniform float uIntensity;    // Current intensity from time ramp

void main() {
    // When intensity is 0 (no effect active), show normal scene
    // When intensity > 0, apply ripple distortion
    
    if (uIntensity < 0.001) {
        gl_FragColor = texture2D(textureSampler, vUV);
        return;
    }
    
    // Calculate distance from center
    vec2 toCenter = vUV - uCenter;
    float dist = length(toCenter);
    
    // Calculate ripple wave based on distance and time
    float wave = sin(dist * uFrequency * 6.28318 - uTime * uSpeed * 6.28318) * uAmplitude;
    
    // Apply intensity falloff
    wave *= uIntensity;
    
    // Apply radial falloff (effect weakens further from center)
    float falloff = 1.0 - smoothstep(0.0, 0.5, dist);
    wave *= falloff;
    
    // Distort UV coordinates radially
    vec2 dir = normalize(toCenter + vec2(0.0001)); // avoid division by zero
    vec2 distortedUV = vUV + dir * wave;
    
    // Sample the texture with distorted coordinates
    gl_FragColor = texture2D(textureSampler, distortedUV);
}
`;

// Default configurations
const DEFAULT_RIPPLE: Required<Omit<RippleConfig, 'timeRamp'>> & { timeRamp: TimeRamp } = {
  centerX: 0.5,
  centerY: 0.5,
  duration: 0.5,
  amplitude: 0.03,
  frequency: 10,
  speed: 1.5,
  timeRamp: TimeRamps.explosion,
};

const DEFAULT_SHAKE: Required<Omit<ShakeConfig, 'timeRamp'>> & { timeRamp: TimeRamp } = {
  duration: 0.3,
  amplitude: 0.15,
  frequency: 30,
  zoomAmount: 1.03,
  timeRamp: TimeRamps.easeOut,
  rotationJitter: false,
  rotationAmplitude: 0.005,
};

let effectIdCounter = 0;

function generateEffectId(type: string): string {
  return `${type}_${++effectIdCounter}_${Date.now()}`;
}

export class ImpactEffectManager {
  private scene: Scene;
  private camera: Camera;
  private eventBus?: EventBus;
  
  // Effect state
  private activeEffects: Map<string, ActiveEffect> = new Map();
  
  // Ripple post-process
  private ripplePost: PostProcess | null = null;
  private rippleAttached: boolean = false;
  private currentRippleState = {
    centerX: 0.5,
    centerY: 0.5,
    time: 0,
    amplitude: 0,
    frequency: 10,
    speed: 1.5,
    intensity: 0,
  };
  
  // Shake state
  private shakeOffset: Vector3 = Vector3.Zero();
  private originalCameraPosition: Vector3 | null = null;
  private originalFov: number | null = null;
  private isShaking: boolean = false;

  constructor(scene: Scene, camera: Camera, eventBus?: EventBus) {
    this.scene = scene;
    this.camera = camera;
    this.eventBus = eventBus;
    
    this.initializeRippleShader();
    this.setupEventListeners();
    
    logger.debug.log('✅ ImpactEffectManager initialized');
  }

  private initializeRippleShader(): void {
    // Register the shader
    Effect.ShadersStore['rippleFragmentShader'] = RIPPLE_FRAGMENT_SHADER;
    
    // Create post-process and immediately attach to camera
    // We keep it attached permanently and control via intensity uniform
    this.ripplePost = new PostProcess(
      'impactRipple',
      'ripple',
      ['uCenter', 'uTime', 'uAmplitude', 'uFrequency', 'uSpeed', 'uIntensity'],
      null,
      1.0,
      this.camera,  // Attach to camera immediately
      undefined,
      this.scene.getEngine()
    );
    
    // Mark as attached
    this.rippleAttached = true;
    
    this.ripplePost.onApply = (effect) => {
      effect.setFloat2('uCenter', this.currentRippleState.centerX, this.currentRippleState.centerY);
      effect.setFloat('uTime', this.currentRippleState.time);
      effect.setFloat('uAmplitude', this.currentRippleState.amplitude);
      effect.setFloat('uFrequency', this.currentRippleState.frequency);
      effect.setFloat('uSpeed', this.currentRippleState.speed);
      effect.setFloat('uIntensity', this.currentRippleState.intensity);
    };
    
    logger.debug.debug('🌊 Ripple shader initialized and attached');
  }

  private setupEventListeners(): void {
    if (!this.eventBus) return;
    
    this.eventBus.on('impact:ripple', (data: ImpactEffectEvent) => {
      const center = this.resolveScreenCenter(data);
      this.triggerRipple({
        centerX: center.x,
        centerY: center.y,
        amplitude: DEFAULT_RIPPLE.amplitude * (data.intensity ?? 1),
      });
    });
    
    this.eventBus.on('impact:shake', (data: ImpactEffectEvent) => {
      this.triggerShake({
        amplitude: DEFAULT_SHAKE.amplitude * (data.intensity ?? 1),
      });
    });
    
    this.eventBus.on('impact:explosion', (data: ImpactEffectEvent) => {
      const center = this.resolveScreenCenter(data);
      this.triggerExplosion({
        screenCenter: center,
        intensity: data.intensity,
      });
    });
    
    logger.debug.debug('🔌 Impact effect event listeners registered');
  }

  private resolveScreenCenter(data: ImpactEffectEvent): { x: number; y: number } {
    if (data.screenCenter) {
      return data.screenCenter;
    }
    if (data.position) {
      return this.worldToScreen(data.position);
    }
    return { x: 0.5, y: 0.5 };
  }

  /**
   * Convert world position to normalized screen-space coordinates
   */
  public worldToScreen(worldPos: Vector3): { x: number; y: number } {
    // Get the view-projection matrix
    const viewMatrix = this.camera.getViewMatrix();
    const projMatrix = this.camera.getProjectionMatrix();
    const transform = viewMatrix.multiply(projMatrix);
    
    // Transform world position to clip space
    const clipPos = Vector3.TransformCoordinates(worldPos, transform);
    
    // Convert to normalized screen coordinates (0-1)
    const x = (clipPos.x + 1) / 2;
    const y = (clipPos.y + 1) / 2; // Note: Y might need to be flipped depending on coordinate system
    
    return { 
      x: Math.max(0, Math.min(1, x)), 
      y: Math.max(0, Math.min(1, y)) 
    };
  }

  /**
   * Trigger a ripple distortion effect
   * @returns Effect ID for cancellation
   */
  public triggerRipple(config: RippleConfig): string {
    const id = generateEffectId('ripple');
    
    const resolvedConfig: Required<RippleConfig> = {
      centerX: config.centerX,
      centerY: config.centerY,
      duration: config.duration ?? DEFAULT_RIPPLE.duration,
      amplitude: config.amplitude ?? DEFAULT_RIPPLE.amplitude,
      frequency: config.frequency ?? DEFAULT_RIPPLE.frequency,
      speed: config.speed ?? DEFAULT_RIPPLE.speed,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'explosion'),
    };
    
    const effect: ActiveEffect = {
      id,
      type: 'ripple',
      startTime: performance.now(),
      durationMs: resolvedConfig.duration * 1000,
      config: resolvedConfig,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'explosion'),
    };
    
    this.activeEffects.set(id, effect);
    
    // Update current ripple state
    this.currentRippleState.centerX = resolvedConfig.centerX;
    this.currentRippleState.centerY = resolvedConfig.centerY;
    this.currentRippleState.amplitude = resolvedConfig.amplitude;
    this.currentRippleState.frequency = resolvedConfig.frequency;
    this.currentRippleState.speed = resolvedConfig.speed;
    
    logger.debug.debug(`🌊 Ripple effect started: ${id} at (${resolvedConfig.centerX.toFixed(2)}, ${resolvedConfig.centerY.toFixed(2)})`);
    
    return id;
  }

  /**
   * Trigger a screen shake effect
   * @returns Effect ID for cancellation
   */
  public triggerShake(config: ShakeConfig = {}): string {
    const id = generateEffectId('shake');
    
    const resolvedConfig: Required<ShakeConfig> = {
      duration: config.duration ?? DEFAULT_SHAKE.duration,
      amplitude: config.amplitude ?? DEFAULT_SHAKE.amplitude,
      frequency: config.frequency ?? DEFAULT_SHAKE.frequency,
      zoomAmount: config.zoomAmount ?? DEFAULT_SHAKE.zoomAmount,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'easeOut'),
      rotationJitter: config.rotationJitter ?? DEFAULT_SHAKE.rotationJitter,
      rotationAmplitude: config.rotationAmplitude ?? DEFAULT_SHAKE.rotationAmplitude,
    };
    
    // Store original camera state if this is the first shake
    if (!this.isShaking) {
      this.originalCameraPosition = this.camera.position.clone();
      if (this.camera instanceof ArcRotateCamera) {
        this.originalFov = this.camera.fov;
        // Zoom in slightly to hide edges during shake
        this.camera.fov = this.camera.fov / resolvedConfig.zoomAmount;
      }
      this.isShaking = true;
    }
    
    const effect: ActiveEffect = {
      id,
      type: 'shake',
      startTime: performance.now(),
      durationMs: resolvedConfig.duration * 1000,
      config: resolvedConfig,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'easeOut'),
      originalPosition: this.originalCameraPosition?.clone(),
      originalFov: this.originalFov ?? undefined,
    };
    
    this.activeEffects.set(id, effect);
    
    logger.debug.debug(`📳 Shake effect started: ${id}, amplitude=${resolvedConfig.amplitude.toFixed(3)}`);
    
    return id;
  }

  /**
   * Trigger combined explosion effect (ripple + shake)
   * @returns Array of effect IDs
   */
  public triggerExplosion(config: ExplosionConfig = {}): string[] {
    const intensity = config.intensity ?? 1.0;
    const ids: string[] = [];
    
    // Resolve screen center
    let center = { x: 0.5, y: 0.5 };
    if (config.screenCenter) {
      center = config.screenCenter;
    } else if (config.worldPosition) {
      center = this.worldToScreen(config.worldPosition);
    }
    
    // Trigger ripple
    const rippleId = this.triggerRipple({
      centerX: center.x,
      centerY: center.y,
      amplitude: (config.ripple?.amplitude ?? DEFAULT_RIPPLE.amplitude) * intensity,
      duration: config.ripple?.duration ?? 0.6,
      frequency: config.ripple?.frequency ?? 8,
      speed: config.ripple?.speed ?? 1.2,
      timeRamp: config.ripple?.timeRamp ?? 'explosion',
    });
    ids.push(rippleId);
    
    // Trigger shake (unless explicitly disabled)
    if (config.shake !== null) {
      const shakeId = this.triggerShake({
        amplitude: (config.shake?.amplitude ?? DEFAULT_SHAKE.amplitude) * intensity,
        duration: config.shake?.duration ?? 0.4,
        frequency: config.shake?.frequency ?? 25,
        zoomAmount: config.shake?.zoomAmount ?? 1.05,
        timeRamp: config.shake?.timeRamp ?? 'punch',
      });
      ids.push(shakeId);
    }
    
    logger.debug.debug(`💥 Explosion effect triggered with ${ids.length} sub-effects`);
    
    return ids;
  }

  /**
   * Cancel an active effect by ID
   */
  public cancelEffect(id: string): void {
    const effect = this.activeEffects.get(id);
    if (!effect) return;
    
    this.activeEffects.delete(id);
    
    if (effect.type === 'shake') {
      this.checkAndRestoreShake();
    }
    
    logger.debug.debug(`❌ Effect cancelled: ${id}`);
  }

  /**
   * Cancel all active effects
   */
  public cancelAll(): void {
    this.activeEffects.clear();
    this.restoreShakeState();
    this.currentRippleState.intensity = 0;
    // Post-process stays attached, just set intensity to 0
    
    logger.debug.debug('❌ All effects cancelled');
  }

  /**
   * Check if any effects are currently active
   */
  public hasActiveEffects(): boolean {
    return this.activeEffects.size > 0;
  }

  /**
   * Get count of active effects
   */
  public getActiveEffectCount(): number {
    return this.activeEffects.size;
  }

  /**
   * Update all active effects. Call this every frame.
   * @param deltaTime Time since last frame in seconds
   */
  public update(deltaTime: number): void {
    const now = performance.now();
    const completedEffects: string[] = [];
    
    // Track combined shake offset
    let totalShakeX = 0;
    let totalShakeY = 0;
    let totalShakeZ = 0;
    let hasActiveShake = false;
    
    // Track ripple state (use most recent/strongest ripple)
    let maxRippleIntensity = 0;
    let activeRippleEffect: ActiveEffect | null = null;
    
    for (const [id, effect] of this.activeEffects) {
      const elapsed = now - effect.startTime;
      const progress = Math.min(1, elapsed / effect.durationMs);
      
      if (progress >= 1) {
        completedEffects.push(id);
        continue;
      }
      
      const intensity = effect.timeRamp(progress);
      
      if (effect.type === 'ripple') {
        // Track the ripple with highest intensity
        if (intensity > maxRippleIntensity) {
          maxRippleIntensity = intensity;
          activeRippleEffect = effect;
          
          const config = effect.config as Required<RippleConfig>;
          this.currentRippleState.centerX = config.centerX;
          this.currentRippleState.centerY = config.centerY;
          this.currentRippleState.amplitude = config.amplitude;
          this.currentRippleState.frequency = config.frequency;
          this.currentRippleState.speed = config.speed;
          this.currentRippleState.time = progress;
          this.currentRippleState.intensity = intensity;
        }
      } else if (effect.type === 'shake') {
        hasActiveShake = true;
        const config = effect.config as Required<ShakeConfig>;
        
        // Calculate shake offset using sine waves
        const time = elapsed / 1000;
        const freq = config.frequency;
        
        // Use multiple frequencies for more organic shake
        const shakeX = Math.sin(time * freq * 2 * Math.PI) * 0.7 +
                       Math.sin(time * freq * 1.3 * 2 * Math.PI) * 0.3;
        const shakeY = Math.cos(time * freq * 2 * Math.PI) * 0.6 +
                       Math.cos(time * freq * 1.7 * 2 * Math.PI) * 0.4;
        const shakeZ = Math.sin(time * freq * 0.7 * 2 * Math.PI) * 0.3;
        
        totalShakeX += shakeX * config.amplitude * intensity;
        totalShakeY += shakeY * config.amplitude * intensity;
        totalShakeZ += shakeZ * config.amplitude * intensity * 0.5;
      }
    }
    
    // Apply combined shake
    if (hasActiveShake && this.originalCameraPosition) {
      this.shakeOffset.set(totalShakeX, totalShakeY, totalShakeZ);
      
      // For ArcRotateCamera, we modify the target instead of position
      if (this.camera instanceof ArcRotateCamera) {
        // Store original target if needed
        const baseTarget = this.camera.target.clone();
        this.camera.target = baseTarget.add(this.shakeOffset);
      } else {
        this.camera.position = this.originalCameraPosition.add(this.shakeOffset);
      }
    }
    
    // Update ripple intensity (fade to 0 if no active ripples)
    if (!activeRippleEffect) {
      this.currentRippleState.intensity = Math.max(0, this.currentRippleState.intensity - deltaTime * 5);
    }
    
    // Clean up completed effects
    for (const id of completedEffects) {
      this.activeEffects.delete(id);
    }
    
    // Restore shake state if no more shake effects
    if (!hasActiveShake && this.isShaking) {
      this.restoreShakeState();
    }
  }

  private checkAndRestoreShake(): void {
    // Check if any shake effects remain
    let hasShake = false;
    for (const effect of this.activeEffects.values()) {
      if (effect.type === 'shake') {
        hasShake = true;
        break;
      }
    }
    
    if (!hasShake) {
      this.restoreShakeState();
    }
  }

  private restoreShakeState(): void {
    if (!this.isShaking) return;
    
    // Restore camera state
    if (this.originalCameraPosition) {
      if (this.camera instanceof ArcRotateCamera) {
        // ArcRotateCamera doesn't have a simple position restore
        // The target will naturally reset on next update
      } else {
        this.camera.position = this.originalCameraPosition.clone();
      }
    }
    
    if (this.originalFov !== null && this.camera instanceof ArcRotateCamera) {
      this.camera.fov = this.originalFov;
    }
    
    this.originalCameraPosition = null;
    this.originalFov = null;
    this.isShaking = false;
    this.shakeOffset.set(0, 0, 0);
    
    logger.debug.debug('📳 Shake state restored');
  }

  /**
   * Clean up all resources
   */
  public dispose(): void {
    this.cancelAll();
    
    if (this.ripplePost) {
      if (this.rippleAttached) {
        this.camera.detachPostProcess(this.ripplePost);
      }
      this.ripplePost.dispose(this.camera);
      this.ripplePost = null;
    }
    
    logger.debug.log('🗑️ ImpactEffectManager disposed');
  }
}
